import { useNavigate } from 'react-router-dom'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { Card } from '@repo/ui'
import { BookOpenIcon, BuildingIcon, CompassIcon, LifeBuoyIcon, UserIcon } from 'lucide-react'

// Direct subpath, not the `components` barrel: that barrel re-exports onboarding, which
// reaches back into components/input/Search — a cycle.
import { openToursDialog } from 'components/onboarding'
import { useCanAccess } from 'hooks'
import { ROUTES } from 'routes'

/**
 * The "get oriented" and "settings" rows below the checklist (VRG-338), modelled on the
 * Vergence home page.
 *
 * WHAT THESE ARE FOR. The checklist is a sequence of things to DO, in order. These are the
 * places a first-run user asks for by name and cannot yet find in a sidebar they have not
 * learned: what is this product, where do I get help, where is my account. Cards rather than
 * more checklist rows, deliberately — a card is a destination you may or may not want, a
 * checklist row is a task with a right answer, and mixing the two makes the checklist look
 * longer and less finishable than it is.
 *
 * ⚠️ THE UNRESOLVED QUESTION THIS RAISES. Vergence's version of this page is PERMANENT. Vera's
 * Home retires — checklist complete or dismissed — which means everything here disappears on
 * the day setup finishes. That is either fine (these are first-run orientation, and the
 * durable versions live in the Help hub, VRG-342, and the sidebar) or it is an argument that
 * Home should stop retiring. The epic has already rejected a permanent Home twice, so the
 * cards are built to be defensible under the retiring model: nothing here is the ONLY route to
 * anything, so losing them costs discovery, never access.
 *
 * That decision is explicitly still open (user, 2026-08-25: "lets add the VERGENCE sections to
 * home and then we can decide on permanent or not"). Settle it before this ships.
 */

type HomeCard = {
  label: string
  description: string
  /** Where it goes. Omit when the card opens something in place — pass `onClick` instead. */
  to?: string
  /** For a card that opens a dialog rather than navigating (the tours catalog). */
  onClick?: () => void
  icon: React.ReactNode
  /** Rendered only when the viewer holds this. Omitted = always shown. */
  permission?: Parameters<typeof useCanAccess>[0]['action']
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body4"
    sx={{
      // A section LABEL, not a heading someone reads as a title: uppercase, tracked out, and
      // secondary, so it groups the cards beneath it without competing with the page title.
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'text.secondary',
    }}
  >
    {children}
  </Typography>
)

const CardLink = ({ card }: { card: HomeCard }) => {
  const navigate = useNavigate()
  const theme = useTheme()

  const go = () => (card.onClick ? card.onClick() : navigate(card.to as string))

  /*
    The shared `Card`, not a hand-rolled bordered Box — these sit directly beneath the
    checklist and tours cards, and a second, subtly different card surface on the same page
    reads as a mistake. Card owns the border, radius, paper background and padding, so
    matching it by construction is the only way the two stay identical after someone retunes
    it (§28/§47 — reuse the shared component before styling a Box).

    `onClick` + role/tabIndex/onKeyDown rather than `component={RouterLink}`: Card forwards a
    NAMED prop list and silently drops anything else, so a `component` prop would vanish
    without a type error. Its own docblock spells out this contract — a card with onClick
    alone renders a div no keyboard can reach, which would make the focus ring below dead
    code.
  */
  return (
    <Card
      onClick={go}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          go()
        }
      }}
      sx={{
        flex: '1 1 0',
        // 180 so THREE fit across the 720 reading column: (720 - 2x12 gap) / 3 = 232 each, and
        // a floor above that wraps the third card onto its own row where `flex:1 1 0` stretches
        // it to full width — a lone wide card under two narrow ones, which looks like a bug
        // rather than a grid. Measured at 240 and it did exactly that to Tag shop.
        minWidth: 180,
        cursor: 'pointer',
        // The Card default (24px). It was 16 on the theory that these are one-line
        // destinations, which read as cramped next to the 24px checklist and tours cards
        // directly above — the whole point of moving onto Card was that this row should not
        // look like a different, smaller kind of surface.
        padding: 6,
        transition: 'border-color 120ms, box-shadow 120ms',
        // An ACTION/NAVIGATION card (§34): brand border + ring on hover AND focus-visible.
        // Not the tint used for SELECTABLE cards — clicking this goes somewhere and leaves
        // no persistent selected state, so the border is free to mean "this is what you are
        // about to open". The focus ring is the only cue a keyboard user gets.
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          outline: 'none',
        },
      }}
    >
      {/* Icon ABOVE the label rather than beside it. Side-by-side, the icon stole ~30px of a
          224px card from the text that had to wrap in it; stacked, the description gets the
          full width and the card gains the vertical presence it was missing. */}
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{card.icon}</Box>
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="body2">{card.label}</Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {card.description}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}

const Section = ({ title, cards }: { title: string; cards: HomeCard[] }) => {
  const visible = cards.filter(Boolean)
  if (!visible.length) return null

  return (
    <Stack spacing={3}>
      <SectionHeading>{title}</SectionHeading>
      {/* Wraps rather than a fixed 3-up grid: the card count varies with permissions, and a
          rigid grid leaves a hole where a gated card used to be. */}
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }} useFlexGap>
        {visible.map((card) => (
          <CardLink key={card.label} card={card} />
        ))}
      </Stack>
    </Stack>
  )
}

export const HomeSections = ({ hasChecklist }: { hasChecklist: boolean }) => {
  const { isHasAccess: canManageCompany } = useCanAccess({ action: 'manage_company' })

  const orient: HomeCard[] = [
    {
      label: 'Documentation',
      description: 'Guides, how-tos, and the glossary',
      to: ROUTES.DOCS,
      icon: <BookOpenIcon size={22} strokeWidth={1} />,
    },
    {
      label: 'Support',
      description: 'Ask a question or report a problem',
      to: ROUTES.SUPPORT,
      icon: <LifeBuoyIcon size={22} strokeWidth={1} />,
    },
    /*
      TOURS, not Tag shop. Tag shop was already "Order your first tags" one card up in the
      checklist, so the page asked for the same action twice in two different visual languages
      — and the checklist row is the better of the two, because it knows whether you have done
      it. An orientation row should not duplicate a checklist step.

      Tours earns the slot instead: it is the one thing here that is neither a task nor a
      destination, and it is the answer to "how do I learn this" that a first-run user asks
      before any specific question. Opens the catalog dialog in place rather than navigating —
      the permanent home for tours is the Help hub (VRG-342), which does not exist yet.
    */
    // Only when the standalone Getting-started block is NOT rendered. See HomePage for the
    // rule: tours appear exactly once, as a block for someone with no checklist and as this
    // card for someone who has one. Both would be the duplication this row just removed Tag
    // shop for.
    ...(hasChecklist
      ? [
          {
            label: 'Tours',
            // One line at the 3-up card width — the only description in the row that wrapped,
            // which made the row ragged. Character count is the WRONG measure here: "Guided
            // walkthroughs of the product" is 34, exactly Support's, and still wrapped, because
            // "walkthroughs" is a 12-character unbreakable word while Support's 34 are all
            // short. Measured instead: 182px available, this renders at 156.
            description: 'Guided product walkthroughs',
            onClick: openToursDialog,
            icon: <CompassIcon size={22} strokeWidth={1} />,
          },
        ]
      : []),
  ]

  const settings: HomeCard[] = [
    {
      label: 'Profile',
      description: 'Your details, appearance, and notifications',
      to: ROUTES.SETTINGS_PERSONAL_PROFILE,
      icon: <UserIcon size={22} strokeWidth={1} />,
    },
    ...(canManageCompany
      ? [
          {
            // "Company", not "Organization". Organization is the industry-default word
            // (GitHub, Linear, Vercel) and is what this card said when it was copied from
            // Vergence — but Vera says Company absolutely everywhere else: the DB table, the
            // company_admin role, the manage_company permission, 23 routes, the internal
            // Companies console. One card in a different dialect is just a bug.
            label: 'Company',
            description: 'Members, roles, billing, and integrations',
            to: ROUTES.SETTINGS_COMPANY_GENERAL,
            icon: <BuildingIcon size={22} strokeWidth={1} />,
          },
        ]
      : []),
  ]

  return (
    <>
      <Section title="Get oriented" cards={orient} />
      <Section title="Settings" cards={settings} />
    </>
  )
}
